// src/services/mapsService.js

/**
 * Open in native maps application with smart fallback
 * Detects device type and opens appropriate maps application
 */
export const openInMapsApp = (from, to, destinationType = 'pickup') => {
  if (!from || !to) {
    console.warn('Invalid location data for maps');
    return;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = /android/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  const toLocation = `${to.lat},${to.lng}`;
  const fromLocation = `${from.lat},${from.lng}`;

  if (isAndroid) {
    // Android: Try native Google Maps first, fallback to web
    const nativeUrl = `google.navigation:q=${toLocation}&mode=d`;
    const fallbackUrl = `https://www.google.com/maps/dir/?api=1&origin=${fromLocation}&destination=${toLocation}&travelmode=driving`;

    // Try native app
    window.location.href = nativeUrl;

    // Fallback after 500ms if native app doesn't open
    setTimeout(() => {
      window.location.href = fallbackUrl;
    }, 500);
  } else if (isIOS) {
    // iOS: Use Apple Maps
    const url = `maps://maps.apple.com/?saddr=${fromLocation}&daddr=${toLocation}&dirflg=d`;
    window.location.href = url;
  } else {
    // Web/Desktop: Open Google Maps web version
    const url = `https://www.google.com/maps/dir/?api=1&origin=${fromLocation}&destination=${toLocation}&travelmode=driving`;
    window.open(url, '_blank');
  }
};

/**
 * Generate navigation URL for share or redirect
 */
export const generateNavigationUrl = (from, to) => {
  if (!from || !to) return null;

  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  if (isIOS) {
    return `maps://maps.apple.com/?saddr=${from.lat},${from.lng}&daddr=${to.lat},${to.lng}&dirflg=d`;
  } else {
    return `https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`;
  }
};

/**
 * Reverse geocode coordinates to address
 * Uses OpenStreetMap Nominatim service (free, no API key needed)
 */
export const reverseGeocode = async (location) => {
  if (!location) return 'Unknown location';

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`
    );
    const data = await response.json();

    if (data.address) {
      // Construct readable address from parts
      const parts = [
        data.address.road,
        data.address.suburb || data.address.city_district,
        data.address.city || data.address.town,
      ].filter(Boolean);

      return parts.length > 0 ? parts.join(', ') : data.address.name || 'Unknown location';
    }

    return 'Unknown location';
  } catch (error) {
    console.error('Geocoding error:', error);
    return 'Unknown location';
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 * Returns distance in kilometers
 */
export const calculateDistance = (from, to) => {
  if (!from || !to) return 0;

  const R = 6371; // Earth radius in km
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((from.lat * Math.PI) / 180) *
      Math.cos((to.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return parseFloat(distance.toFixed(2));
};

/**
 * Estimate ETA based on distance and average speed
 * Returns time in minutes
 */
export const estimateETA = (distance, avgSpeedKmh = 40) => {
  if (!distance) return 0;
  const minutes = (distance / avgSpeedKmh) * 60;
  return Math.ceil(minutes);
};

/**
 * Calculate fare based on distance and rate
 * Simple model: base fare + per-km charge
 */
export const calculateFare = (distance, baseFare = 2.5, perKmRate = 1.5) => {
  if (!distance) return baseFare;
  const fare = baseFare + distance * perKmRate;
  return parseFloat(fare.toFixed(2));
};

/**
 * Detect if device is mobile
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Request geolocation with timeout
 */
export const getCurrentLocation = (timeout = 10000) => {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation not available'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: timeout,
        maximumAge: 5000,
      }
    );
  });
};

export default {
  openInMapsApp,
  generateNavigationUrl,
  reverseGeocode,
  calculateDistance,
  estimateETA,
  calculateFare,
  isMobileDevice,
  getCurrentLocation,
};
